import { Button, Grid, InputAdornment, MenuItem, TextField, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { BillingsWithCashFlow, CashFlowInstallment, CashFlowOptions } from '../CashFlow'
import { allowMoneyFormat, allowNumbers, formatNumberToMoney, roundTwoDecimals } from '../../../../Utils'
import { useCallback } from 'react'
import { DeleteForever, NoteAdd } from '@mui/icons-material'

type Props = {
  disableSetCashFlowButton: boolean
  error: boolean
  form: BillingsWithCashFlow
  netValue: number
  updateCashFlowOptions: (updates: Partial<CashFlowOptions>) => void
  updateCashFlowInstallments: (updates: CashFlowInstallment[]) => void
}

export default function CashFlowOptionsComponent({ disableSetCashFlowButton, error, form, netValue, updateCashFlowOptions, updateCashFlowInstallments }: Props) {
  const setCashFlow = useCallback(() => {
    const _cashFlowInstallments: CashFlowInstallment[] = []

    // Calculating net value per bill
    const dueValuePerBill = form.cashFlowOptions.mode === 'numberOfBills' ? roundTwoDecimals(netValue / form.cashFlowOptions.numberOfBills.value) : form.cashFlowOptions.setValue.value!
    // Number of installments
    const numberOfInstallments = form.cashFlowOptions.mode === 'numberOfBills' ? form.cashFlowOptions.numberOfBills.value : Math.floor(netValue / form.cashFlowOptions.setValue.value)
    // Gross value per bill
    const grossValue = roundTwoDecimals(form.billing.totalBilling.value / numberOfInstallments)
    // Remainder
    var remainder = netValue

    for (var counter = 0; counter < numberOfInstallments; counter++) {
      remainder -= dueValuePerBill

      _cashFlowInstallments.push({
        installment_number: counter + 1,
        comment: null,
        due_date: form.cashFlowOptions.dateOfFirstCashflow!.plus({ month: counter }),
        dueValue: {
          value: dueValuePerBill,
          label: formatNumberToMoney(dueValuePerBill),
        },
        fk_id_payment_method: form.billing.paymentMethod,
        grossValue: {
          value: grossValue,
          label: formatNumberToMoney(grossValue),
        },
        paid_date: null,
        paid_value: 0
      })
    }

    // Remainder
    if (remainder > 0) {
      // Rounding the remainder
      remainder = roundTwoDecimals(remainder)

      // Check remainder option
      if (form.cashFlowOptions.remainder === 'first-bill') {
        _cashFlowInstallments[0].dueValue.value += remainder
        _cashFlowInstallments[0].dueValue.label = formatNumberToMoney(_cashFlowInstallments[0].dueValue.value)
      }

      if (form.cashFlowOptions.remainder === 'last-bill') {
        _cashFlowInstallments[_cashFlowInstallments.length - 1].dueValue.value += remainder
        _cashFlowInstallments[_cashFlowInstallments.length - 1].dueValue.label = formatNumberToMoney(_cashFlowInstallments[_cashFlowInstallments.length - 1].dueValue.value)
      }

      if (form.cashFlowOptions.remainder === 'equal') {
        const remainderShare = roundTwoDecimals(remainder / numberOfInstallments) || 0.01

        for (var _counter = 0; _counter < numberOfInstallments; _counter++) {
          // console.log(remainder)

          remainder -= remainderShare

          if (remainder <= 0) break
          // Final installment
          if (_counter === numberOfInstallments - 1) {
            _cashFlowInstallments[_counter].dueValue.value += remainder
            _cashFlowInstallments[_counter].dueValue.label = formatNumberToMoney(_cashFlowInstallments[_counter].dueValue.value)
          }

          _cashFlowInstallments[_counter].dueValue.value += remainderShare
          _cashFlowInstallments[_counter].dueValue.label = formatNumberToMoney(_cashFlowInstallments[_counter].dueValue.value)
        }
      }
    }

    updateCashFlowInstallments(_cashFlowInstallments)
  }, [form, netValue, updateCashFlowInstallments])

  return <>
    {
      Boolean(form.billing.id) ?
        null
        :
        <>
          <Grid item xs={12}>
            <Typography>
              Cash Flow
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              id='form-field-mode'
              label='Mode'
              value={form.cashFlowOptions.mode}
              onChange={(e) => updateCashFlowOptions({
                mode: e.target.value as 'numberOfBills' | 'setValue'
              })}
              select
            >
              <MenuItem id='cashflow-mode-number-of-bills' value='numberOfBills'>Number of bills</MenuItem>
              <MenuItem id='cashflow-mode-set-value' value='setValue'>Set value per bill</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              id='form-field-number-of-bills-value'

              label={form.cashFlowOptions.mode === 'numberOfBills' ? 'Number of Bills' : 'Value'}
              value={form.cashFlowOptions[form.cashFlowOptions.mode].label}
              onChange={(e) => updateCashFlowOptions({
                numberOfBills: {
                  value: form.cashFlowOptions.mode === 'numberOfBills' ? allowNumbers(e.target.value.trim()) : allowMoneyFormat(e.target.value.trim()),
                  label: e.target.value.trim()
                }
              })}
              error={error}
              onBlur={() => {
                if (form.cashFlowOptions.mode === 'setValue' && !isNaN(form.cashFlowOptions.setValue.value)) {
                  updateCashFlowOptions({
                    setValue: {
                      ...form.cashFlowOptions.setValue,
                      label: formatNumberToMoney(form.cashFlowOptions.setValue.value)
                    }
                  })
                }
              }}
              onFocus={() => {
                if (form.cashFlowOptions.mode === 'setValue' && form.cashFlowOptions.setValue.value) {
                  updateCashFlowOptions({
                    setValue: {
                      ...form.cashFlowOptions.setValue,
                      label: form.cashFlowOptions.setValue.value.toFixed(2)
                    }
                  })
                }
              }}
              InputProps={
                form.cashFlowOptions.mode === 'numberOfBills' ? {} : { startAdornment: <InputAdornment position='start'>{form.billing.currency.sign}</InputAdornment> }
              }
            />
          </Grid>
          {
            form.cashFlowOptions.mode === 'numberOfBills' && !isNaN(netValue) ?
              <Grid item xs={12}>
                <Typography textAlign={'right'}>
                  <b>Per bill:</b> {form.billing.currency.sign} {roundTwoDecimals(netValue / form.cashFlowOptions.numberOfBills.value)}
                </Typography>
              </Grid>
              :
              <></>
          }
          <Grid item xs={5}>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DatePicker
                slotProps={{ textField: { id: 'form-field-first-bill-due-date' } }}
                format="dd-MMM-yy"
                label="First bill's due date"
                value={form.cashFlowOptions.dateOfFirstCashflow}
                onChange={(newValue) => updateCashFlowOptions({
                  dateOfFirstCashflow: newValue
                })}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={7}>
            <TextField
              id='form-field-remainder-options'
              label='Remainder options'
              value={form.cashFlowOptions.remainder}
              onChange={(e) => updateCashFlowOptions({
                remainder: e.target.value as 'first-bill' | 'last-bill' | 'equal'
              })}
              select
              disabled
            >
              <MenuItem id='remainder-option-first-bill' value={'first-bill'}>Add to first bill</MenuItem>
              <MenuItem id='remainder-option-last-bill' value={'last-bill'}>Add to last bill</MenuItem>
              <MenuItem id='remainder-option-equal' value={'equal'}>Distribute equally</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Button
              id='button-set-cash-flow'
              color={form.cashFlowInstallments.length ? 'error' : 'primary'}
              onClick={form.cashFlowInstallments.length ? () => updateCashFlowInstallments([]) : () => setCashFlow()}
              startIcon={form.cashFlowInstallments.length ? <DeleteForever /> : <NoteAdd />}
              disabled={disableSetCashFlowButton}
            >
              {form.cashFlowInstallments.length ? 'Clear Cash Flow' : 'Set Cash Flow'}
            </Button>
          </Grid>
        </>
    }
  </>
}
